import { ReactNode } from 'react';
import { Logo } from '@/components/logos/logo';
import { Editor } from 'slate';
import { hasImage } from '../plugins/with-document-structure';
import type { ToastFunction } from '../utils/mms-utils';

export interface SlashCommand {
  label: string;
  icon: ReactNode;
  description: string;
  action: () => void;
  category: string;
  disabled?: boolean;
  disabledReason?: string;
}

interface SlashCommandHandlers {
  onImageClick: () => void;
  onEmojiClick: () => void;
  onVariableClick: () => void;
  onShortLinkClick: () => void;
  onContactClick: () => void;
  editor: Editor;
  toast: ToastFunction;
}

// Create a factory function to generate commands with proper action handlers
export function createSlashCommands(handlers: SlashCommandHandlers): SlashCommand[] {
  const hasExistingImage = hasImage(handlers.editor);

  return [
    {
      label: 'Emoji',
      icon: <Logo icon="emoji" />,
      description: 'Insert an emoji 😊',
      action: handlers.onEmojiClick,
      category: 'Media'
    },
    {
      label: 'Image or GIF',
      icon: <Logo icon="media" />,
      description: 'Adding media converts to MMS',
      action: () => {
        if (hasExistingImage) {
          handlers.toast({
            title: 'Only one image allowed',
            description: 'Please remove the existing image before adding a new one.',
            variant: 'destructive'
          });
          return;
        }
        handlers.onImageClick();
      },
      category: 'Media',
      disabled: hasExistingImage,
      disabledReason: hasExistingImage ? 'Only one image allowed per message' : undefined
    },
    {
      label: 'Shortlink',
      icon: <Logo icon="shortlink" />,
      description: 'Insert a tracked link',
      action: handlers.onShortLinkClick,
      category: 'Embeds'
    },
    {
      label: 'Variable',
      icon: <Logo icon="insertVariable" />,
      description: 'Insert a personalization variable',
      action: handlers.onVariableClick,
      category: 'Embeds'
    },
    {
      label: 'Discount',
      icon: <Logo icon="discountCode" />,
      description: 'Insert a discount code',
      action: () => {
        console.log('Discount clicked');
      },
      category: 'Embeds'
    },
    {
      label: 'Contact',
      icon: <Logo icon="contactCard" />,
      description: 'Insert contact information',
      action: handlers.onContactClick,
      category: 'Embeds'
    }
  ];
}
